import Select, { type SingleValue, type StylesConfig } from 'react-select';
import { useMemo } from 'react';

export type AppSelectOption = {
  value: string;
  label: string;
};

type AppSelectProps = {
  ariaLabel: string;
  options: AppSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  className?: string;
};

const selectStyles: StylesConfig<AppSelectOption, false> = {
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 16,
    borderColor: state.isFocused ? 'var(--focus-border)' : 'var(--border)',
    backgroundColor: 'var(--bg-soft)',
    boxShadow: state.isFocused ? 'var(--focus-shadow)' : 'none',
    transition: 'border-color 180ms ease, background 180ms ease, box-shadow 180ms ease',
    ':hover': {
      borderColor: state.isFocused ? 'var(--focus-border)' : 'var(--focus-border-hover)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 14px',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--text)',
    fontWeight: 600,
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--text-muted)',
    fontWeight: 600,
  }),
  input: (base) => ({
    ...base,
    color: 'var(--text)',
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? 'var(--text)' : 'var(--text-muted)',
    padding: 8,
    ':hover': {
      color: 'var(--text)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--text-muted)',
    padding: 8,
    ':hover': {
      color: 'var(--text)',
    },
  }),
  menu: (base) => ({
    ...base,
    overflow: 'hidden',
    marginTop: 10,
    backgroundColor: 'var(--card)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    boxShadow: 'var(--shadow-strong)',
  }),
  menuList: (base) => ({
    ...base,
    padding: 6,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 90,
  }),
  option: (base, state) => ({
    ...base,
    margin: '4px 6px',
    borderRadius: 14,
    padding: '10px 12px',
    cursor: 'pointer',
    backgroundColor: state.isSelected
      ? 'var(--accent-soft)'
      : state.isFocused
        ? 'var(--nav-item-active)'
        : 'transparent',
    color: 'var(--text)',
    fontWeight: state.isSelected ? 700 : 600,
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'var(--text-muted)',
  }),
};

export function AppSelect({
  ariaLabel,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  isSearchable = true,
  isClearable = false,
  isDisabled = false,
  menuPlacement = 'auto',
  className,
}: AppSelectProps) {
  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value) ?? null;
  }, [options, value]);

  return (
    <Select<AppSelectOption, false>
      aria-label={ariaLabel}
      className={className}
      classNamePrefix="app-select"
      menuPlacement={menuPlacement}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      menuPosition="fixed"
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      options={options}
      placeholder={placeholder}
      styles={selectStyles}
      value={selectedOption}
      onChange={(next: SingleValue<AppSelectOption>) => onChange(next?.value ?? null)}
      noOptionsMessage={() => 'No options found'}
    />
  );
}
